import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { createContext, memo, useCallback, useContext, useMemo, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../../components/Button';
import { Modal } from '../../../components/Modal';
import useOpen from '../../../hooks/useOpen';
import { T } from '../../../translation/src';
import { formatPrice } from '../../../utils/formatPrice';

export interface OrderConfirmation {
  // The organisation ordered for: "start working" opens its projects.
  organisationId: string;
  // What was ordered, in a few words ("5 projects", "Upgrade to Grow").
  what: React.ReactNode;
  // What is ready to use now ("Your 5 projects are on your balance.").
  ready: React.ReactNode;
  // The amount invoiced, excl. VAT; 0 when nothing is invoiced.
  totalCents: number;
  // Where the invoice goes, when known.
  invoiceEmail?: string | null;
}

export interface OrderConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  // "Start working": the organisation's projects.
  onStart: () => void;
  order?: OrderConfirmation;
}

// After an order (projects bought, a plan started or upgraded): it went through, what was ordered and for how
// much, that it can be used right away, and that the invoice follows by email once sales has checked the order.
const OrderConfirmationModal_: React.FC<OrderConfirmationModalProps> = ({ open, onClose, onStart, order }) => (
  <Modal.Container open={open} onClose={onClose} panelClassName="w-full max-w-md">
    <Modal.Header className="mb-2!" onClose={onClose}>
      <span className="flex items-center gap-2">
        <CheckCircleIcon className="h-6 w-6 text-neon-green-600" />
        <T _str="thank you for your order" swc />
      </span>
    </Modal.Header>
    {order && (
      <Modal.Body className="flex flex-col gap-4 text-sm">
        <div className="flex items-baseline justify-between gap-4 rounded-md border border-gray-300 p-3 dark:border-gray-600">
          <span className="font-medium">{order.what}</span>
          {order.totalCents > 0 && (
            <span className="text-right">
              {formatPrice(order.totalCents)}{' '}
              <span className="text-xs text-gray-500">
                <T _str="excl. VAT" />
              </span>
            </span>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <span className="font-semibold">
            <T _str="You can start right away." />
          </span>
          <span className="text-gray-600 dark:text-gray-300">{order.ready}</span>
        </div>
        {order.totalCents > 0 && (
          <div className="flex flex-col gap-1">
            <span className="font-semibold">
              <T _str="Your invoice follows by email." />
            </span>
            <span className="text-gray-600 dark:text-gray-300">
              {order.invoiceEmail ? (
                <T _str="Our sales team checks every order. Then we send the invoice to {email}." args={{ email: order.invoiceEmail }} />
              ) : (
                <T _str="Our sales team checks every order. Then we send you the invoice." />
              )}
            </span>
          </div>
        )}
      </Modal.Body>
    )}
    <Modal.Footer className="justify-end">
      <Button variant="primary" onClick={onStart}>
        <T _str="start working" swc />
      </Button>
    </Modal.Footer>
  </Modal.Container>
);

export const OrderConfirmationModal = memo(OrderConfirmationModal_);

const OrderConfirmationContext = createContext<{ show: (order: OrderConfirmation) => void }>({ show: () => undefined });

// Holds the confirmation at the top of the app (inside the router: it navigates): the window an order is placed in can be gone once it went through
// (a "no projects left" banner disappears as soon as there are projects again), the confirmation must stay.
export const OrderConfirmationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [order, setOrder] = useState<OrderConfirmation | undefined>(undefined);
  const { open, onOpen, onClose } = useOpen();
  const show = useCallback(
    (next: OrderConfirmation) => {
      setOrder(next);
      onOpen();
    },
    [onOpen],
  );
  const value = useMemo(() => ({ show }), [show]);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // To the projects, to use what was ordered; from inside a project (an upload asked for more projects), the project
  // stays open, so the upload goes on.
  const onStart = useCallback(() => {
    onClose();
    if (order && !matchPath('/organisations/:organisationId/projects/:projectId', pathname)) navigate(`/organisations/${order.organisationId}/projects`);
  }, [navigate, onClose, order, pathname]);
  return (
    <OrderConfirmationContext.Provider value={value}>
      {children}
      <OrderConfirmationModal open={open} onClose={onClose} onStart={onStart} order={order} />
    </OrderConfirmationContext.Provider>
  );
};

export const useOrderConfirmation = () => useContext(OrderConfirmationContext);
